import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import LoaderComponent from '@/components/LoaderComponent';
import CommonImage from '@/components/CommonImage';
import ModalContainer from '@/components/modals/ModalContainer';

import { getPartTitle, renderSyncIcon } from '../utils';

const ConsumptionModal = ({
	fabricWiseMeasurements,
	onClose,
	cadConsumptions,
	isEditable = false,
	onReCostMeasurement,
	selectedMeasurementSize,
	measurementSizeRange,
	selectedConsumptionType,
	toggleConsumptionTypeChange
}) => {
	const [localMeasurements, setLocalMeasurements] = useState({});
	const [localCadConsumptions, setLocalCadConsumptions] = useState([]);
	const [editingRow, setEditingRow] = useState(null);
	const [editingCadRow, setEditingCadRow] = useState(null);
	const [changedPartKeys, setChangedPartKeys] = useState(new Set());
	const [isCadChanged, setIsCadChanged] = useState(false);
	const [cadErrors, setCadErrors] = useState({});
	const [measurementErrors, setMeasurementErrors] = useState({});
	const [hoveredPartKey, setHoveredPartKey] = useState(null);
	const editingRowRef = useRef(null);
	const editingCadRowRef = useRef(null);
	const loader = useSelector((state) => state.loaderStore);
	const [selectedMeasurementType, setSelectedMeasurementType] = useState(selectedConsumptionType);

	const CAD_REQUIRED_FIELDS = ['waste_percentage', 'gsm'];
	const MEASUREMENT_REQUIRED_FIELDS = [
		'part',
		'length_cm',
		'allowance_length_cm',
		'width_cm',
		'allowance_width_cm',
		'number_of_fabric_pieces',
		'gsm',
		'wastage_percentage'
	];

	const validateCadConsumptions = () => {
		const errors = {};
		localCadConsumptions.forEach((cad, index) => {
			CAD_REQUIRED_FIELDS.forEach((field) => {
				const val = cad[field];
				if (val === '' || val === null || val === undefined) {
					if (!errors[index]) errors[index] = {};
					errors[index][field] = 'Required';
				}
			});
		});
		setCadErrors(errors);
		return Object.keys(errors).length === 0;
	};

	const validateMeasurements = () => {
		const errors = {};
		Object.entries(localMeasurements).forEach(([partKey, parts]) => {
			parts.forEach((part, partIndex) => {
				MEASUREMENT_REQUIRED_FIELDS.forEach((field) => {
					const val = part[field];
					if (val === '' || val === null || val === undefined) {
						if (!errors[partKey]) errors[partKey] = {};
						if (!errors[partKey][partIndex]) errors[partKey][partIndex] = {};
						errors[partKey][partIndex][field] = 'Required';
					}
				});
			});
		});
		setMeasurementErrors(errors);
		return Object.keys(errors).length === 0;
	};

	// sync localCadConsumptions from prop
	useEffect(() => {
		if (cadConsumptions && cadConsumptions.length > 0) {
			setLocalCadConsumptions(
				cadConsumptions?.map((cad) => ({
					...cad,
					waste_percentage: ((1 - parseFloat(cad?.cutting_waste_percentage)) * 100)?.toFixed(2)
				}))
			);
			setIsCadChanged(false);
			setCadErrors({});
		}
	}, [cadConsumptions]);

	// click-outside for measurement rows
	useEffect(() => {
		if (!editingRow) return;
		const handleClickOutside = (e) => {
			if (editingRowRef.current && !editingRowRef.current.contains(e.target)) {
				setEditingRow(null);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [editingRow]);

	// click-outside for CAD rows
	useEffect(() => {
		if (editingCadRow === null) return;
		const handleCadClickOutside = (e) => {
			if (editingCadRowRef.current && !editingCadRowRef.current.contains(e.target)) {
				setEditingCadRow(null);
			}
		};
		document.addEventListener('mousedown', handleCadClickOutside);
		return () => document.removeEventListener('mousedown', handleCadClickOutside);
	}, [editingCadRow]);

	useEffect(() => {
		if (Object.keys(fabricWiseMeasurements).length > 0) {
			let tmpObj = {};
			Object.entries(fabricWiseMeasurements).forEach(([partKey, measurements]) => {
				tmpObj[partKey] = measurements.map((measurement) => {
					return {
						...measurement,
						allowance_length_cm: (
							parseFloat(measurement?.final_length_with_allowance_cm) -
								parseFloat(measurement?.length_cm) || 0
						)?.toFixed(2),
						allowance_width_cm: (
							parseFloat(measurement?.final_width_with_allowance_cm) -
								parseFloat(measurement?.width_cm) || 0
						)?.toFixed(2)
					};
				});
			});
			setLocalMeasurements(tmpObj);
			setMeasurementErrors({});
		}
	}, [fabricWiseMeasurements]);

	const handleCellChange = (partKey, partIndex, field, value) => {
		setChangedPartKeys((prev) => new Set([...prev, partKey]));
		setMeasurementErrors((prev) => {
			if (prev[partKey]?.[partIndex]?.[field]) {
				const updated = { ...prev };
				const updatedPart = { ...updated[partKey][partIndex] };
				delete updatedPart[field];
				updated[partKey] = { ...updated[partKey], [partIndex]: updatedPart };
				return updated;
			}
			return prev;
		});
		setLocalMeasurements((prev) => {
			const updated = { ...prev };
			const parts = [...updated[partKey]];
			const part = { ...parts[partIndex], [field]: value };
			part[field] = value;
			part.final_fabric_consumption_kg = calculateTotalConsumption(part)?.toFixed(2);
			parts[partIndex] = part;
			updated[partKey] = parts;
			return updated;
		});
	};

	const handleCadCellChange = (cadIndex, field, value) => {
		setIsCadChanged(true);
		setCadErrors((prev) => {
			if (prev[cadIndex]?.[field]) {
				const updated = { ...prev };
				const updatedRow = { ...updated[cadIndex] };
				delete updatedRow[field];
				updated[cadIndex] = updatedRow;
				return updated;
			}
			return prev;
		});
		setLocalCadConsumptions((prev) => {
			const updated = [...prev];
			let cad = { ...updated[cadIndex], [field]: value };
			let cutting_waste_percentage =
				field === 'waste_percentage' ? 1 - value / 100 : cad.cutting_waste_percentage;
			if (field === 'waste_percentage') {
				cad.cutting_waste_percentage = cutting_waste_percentage;
			}
			const gsm = field === 'gsm' ? parseFloat(value) || 0 : parseFloat(cad.gsm) || 0;
			cad.consumption =
				(cad.fabric_length_inch * 1.1 * (cad.fabric_width_inch + 2) * gsm) /
				1550 /
				1000 /
				cutting_waste_percentage /
				12;
			updated[cadIndex] = cad;
			return updated;
		});
	};

	const isRowEditing = (partKey, partIndex) =>
		editingRow && editingRow.partKey === partKey && editingRow.partIndex === partIndex;

	const handleRowClick = (partKey, partIndex) => {
		if (!isEditable) return;
		setEditingRow({ partKey, partIndex });
	};

	const getEmptyRow = () => ({
		part: '',
		length_cm: '',
		allowance_length_cm: '',
		width_cm: '',
		allowance_width_cm: '',
		number_of_fabric_pieces: '',
		gsm: '',
		wastage_percentage: '',
		final_fabric_consumption_kg: 0
	});

	const handleAddTable = () => {
		const newKey = `custom_${Date.now()}`;
		setLocalMeasurements((prev) => ({
			...prev,
			[newKey]: [getEmptyRow()]
		}));
		setChangedPartKeys((prev) => new Set([...prev, newKey]));
		setEditingRow({ partKey: newKey, partIndex: 0 });
	};

	const handleAddRow = (partKey) => {
		const currentLength = (localMeasurements[partKey] || []).length;
		setLocalMeasurements((prev) => ({
			...prev,
			[partKey]: [...(prev[partKey] || []), getEmptyRow()]
		}));
		setChangedPartKeys((prev) => new Set([...prev, partKey]));
		setEditingRow({ partKey, partIndex: currentLength });
	};

	const handleDeleteRow = (partKey, partIndex) => {
		setLocalMeasurements((prev) => {
			const updated = { ...prev };
			const parts = [...(updated[partKey] || [])];
			parts.splice(partIndex, 1);
			if (parts.length === 0) {
				delete updated[partKey];
			} else {
				updated[partKey] = parts;
			}
			return updated;
		});
		setChangedPartKeys((prev) => new Set([...prev, partKey]));
		if (editingRow?.partKey === partKey && editingRow?.partIndex === partIndex) {
			setEditingRow(null);
		}
		setMeasurementErrors((prev) => {
			if (!prev[partKey]) return prev;
			const updated = { ...prev };
			const updatedRows = { ...updated[partKey] };
			delete updatedRows[partIndex];
			const reindexed = {};
			Object.keys(updatedRows).forEach((k) => {
				const ki = parseInt(k);
				reindexed[ki > partIndex ? ki - 1 : ki] = updatedRows[k];
			});
			updated[partKey] = reindexed;
			return updated;
		});
	};

	const calculateTotalConsumption = (part) => {
		let length = (parseFloat(part.length_cm) || 0) + (parseFloat(part.allowance_length_cm) || 0);
		let width = (parseFloat(part.width_cm) || 0) + (parseFloat(part.allowance_width_cm) || 0);
		let pieces = parseInt(part.number_of_fabric_pieces) || 0;
		let gsm = parseInt(part.gsm) || 0;
		let wastage = parseFloat(part.wastage_percentage) || 0;
		return (length * width * pieces * gsm) / 10000000 / ((100 - wastage) / 100);
	};

	const saveMeasurement = () => {
		if (!validateMeasurements()) return;
		let tmpObj = {};
		Object.entries(localMeasurements).forEach(([partKey, measurements]) => {
			tmpObj[partKey] = measurements.map((measurement) => {
				return {
					...measurement,
					final_length_with_allowance_cm:
						(measurement.length_cm ? parseFloat(measurement?.length_cm) : 0) +
						(measurement.allowance_length_cm ? parseFloat(measurement?.allowance_length_cm) : 0),
					final_width_with_allowance_cm:
						(measurement.width_cm ? parseFloat(measurement?.width_cm) : 0) +
						(measurement.allowance_width_cm ? parseFloat(measurement?.allowance_width_cm) : 0)
				};
			});
		});
		onReCostMeasurement('MEASUREMENT_CHANGE', tmpObj);
		setChangedPartKeys(new Set());
	};

	// isVisible=true → always shown (e.g. "Add Table" icon)
	// isVisible=false → hidden until table is hovered (e.g. "Add Row" icon)
	const renderAddIcon = (onClick, isVisible = true) => (
		<span
			title="Add"
			onClick={onClick}
			style={{
				cursor: 'pointer',
				display: 'inline-flex',
				alignItems: 'center',
				justifyContent: 'center',
				width: 26,
				height: 26,
				borderRadius: '50%',
				background: 'linear-gradient(135deg, #fcd34d 0%, #f59e0b 100%)',
				boxShadow: '0 2px 6px rgba(245,158,11,0.45)',
				color: '#fff',
				fontSize: 18,
				fontWeight: 700,
				lineHeight: 1,
				userSelect: 'none',
				opacity: isVisible ? 1 : 0,
				pointerEvents: isVisible ? 'auto' : 'none',
				transition: 'opacity 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease'
			}}
		>
			+
		</span>
	);

	if (!localMeasurements || Object.keys(localMeasurements).length === 0) {
		return (
			<LoaderComponent loading={false}>
				<ModalContainer className="width-1200-modal">
					<div className="common-popup conumption-modal w-100 body-scroll">
						<div className="common-popup-header border-bottom-0 d-flex justify-content-between">
							<div className="popup-tilte">
								<p className="semibold-14 gray_dark_01">Consumption Breakdown</p>
								<small className="regular-14 gray_dark_01">
									According to the measurement chart, fabric consumption has been calculated
									below:
								</small>
							</div>
							<div className="close-btn bg-white" onClick={onClose}>
								<CommonImage alt="close" src="/icons/close.svg" />
							</div>
						</div>
						<div className="common-popup-body scroll-y-label">
							<p className="text-center p-4">No consumption data available</p>
							{isEditable && (
								<div className="d-flex justify-content-end mb-3 px-2">
									<button
										className="btn btn-sm btn-outline-primary"
										onClick={handleAddTable}
									>
										+ Add Measurement Table
									</button>
								</div>
							)}
						</div>
					</div>
				</ModalContainer>
			</LoaderComponent>
		);
	}

	const renderCadConsumption = () => {
		if (localCadConsumptions && localCadConsumptions?.length > 0) {
			return (
				<div className="consumption-table scroll-x-label mb-3">
					<h3 className="regular-14 mb-2 d-flex align-items-center gap-1">
						{showCheckbox('CAD_CONSUMPTION')}
						CAD Consumptions
						{isCadChanged && (
							<span
								title="Unsaved changes"
								className="sync-icon ml-1 cursor-pointer"
								style={{ display: 'inline-flex', alignItems: 'center' }}
								onClick={() => {
									if (!validateCadConsumptions()) return;
									onReCostMeasurement(
										'CAD_WASTAGE_PERCENTAGE_CHANGE',
										localCadConsumptions
									);
									setIsCadChanged(false);
								}}
							>
								{renderSyncIcon()}
							</span>
						)}
					</h3>
					<table className="table height-sm with-border mb-0">
						<thead>
							<tr>
								<th>Shell No</th>
								<th>Length (inch)</th>
								<th>L. Allow (inch)</th>
								<th>Width (inch)</th>
								<th>W. Allow (inch)</th>
								<th>GSM</th>
								<th>Wastage (%)</th>
								<th>Consumption (Kg)</th>
							</tr>
						</thead>
						<tbody>
							{localCadConsumptions?.map((cad, index) => {
								const isCadRowEditing = editingCadRow === index;
								return (
									<tr
										key={`consumption_row_${index}`}
										ref={isCadRowEditing ? editingCadRowRef : null}
										onClick={() => isEditable && setEditingCadRow(index)}
										style={{ cursor: isEditable ? 'pointer' : 'default' }}
										className={isCadRowEditing ? 'row-editing' : ''}
									>
										<td className="body-part-column">
											<p>{getPartTitle(`part${index + 1}`)}</p>
										</td>
										<td className="length-column">
											<p>{cad?.fabric_length_inch}</p>
										</td>
										<td className="length-with-allowance-column">
											<p>10%</p>
										</td>
										<td className="width-column">
											<p>{cad?.fabric_width_inch}</p>
										</td>
										<td className="width-with-allowance-column">
											<p>2</p>
										</td>
										<td className="gsm-column">
											{isEditable && isCadRowEditing ? (
												<>
													<input
														className={`table-editable-input${cadErrors[index]?.gsm ? ' input-error' : ''}`}
														type="number"
														autoFocus={false}
														value={cad.gsm ?? ''}
														onChange={(e) =>
															handleCadCellChange(index, 'gsm', e.target.value)
														}
													/>
													{cadErrors[index]?.gsm && (
														<small className="red">{cadErrors[index].gsm}</small>
													)}
												</>
											) : (
												<>
													<p className={cadErrors[index]?.gsm ? 'text-danger' : ''}>
														{cad?.gsm}
													</p>
													{cadErrors[index]?.gsm && (
														<small className="red">{cadErrors[index].gsm}</small>
													)}
												</>
											)}
										</td>
										<td className="wastage-column">
											{isEditable && isCadRowEditing ? (
												<>
													<input
														className={`table-editable-input${cadErrors[index]?.waste_percentage ? ' input-error' : ''}`}
														type="number"
														autoFocus
														value={cad.waste_percentage ?? ''}
														onChange={(e) =>
															handleCadCellChange(
																index,
																'waste_percentage',
																e.target.value
															)
														}
													/>
													{cadErrors[index]?.waste_percentage && (
														<small className="red">
															{cadErrors[index].waste_percentage}
														</small>
													)}
												</>
											) : (
												<>
													<p
														className={
															cadErrors[index]?.waste_percentage
																? 'text-danger'
																: ''
														}
													>
														{(
															(1 - parseFloat(cad?.cutting_waste_percentage)) *
															100
														)?.toFixed(2)}
													</p>
													{cadErrors[index]?.waste_percentage && (
														<small className="red">
															{cadErrors[index].waste_percentage}
														</small>
													)}
												</>
											)}
										</td>
										<td className="consumption-column">
											<p>{cad?.consumption?.toFixed(2)}</p>
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
					<div className="total-consumption table-footer p-2">
						<div className="d-flex justify-content-start">
							<p className="regular-14 gray_dark_01 weight-500">Total Consumption</p>
							<p className="regular-14 gray_dark_01 ml-2 weight-500">
								{localCadConsumptions
									?.reduce((total, cad) => total + parseFloat(cad?.consumption || 0), 0)
									?.toFixed(2)}{' '}
								Kg
							</p>
						</div>
					</div>
				</div>
			);
		}
	};

	const renderCell = (partKey, part, partIndex, field, displayValue) => {
		const hasError = measurementErrors[partKey]?.[partIndex]?.[field];
		if (!isEditable || !isRowEditing(partKey, partIndex)) {
			return (
				<>
					<p className={hasError ? 'text-danger' : ''}>{displayValue}</p>
					{hasError && <small className="red">{hasError}</small>}
				</>
			);
		}
		return (
			<>
				<input
					className={`table-editable-input${hasError ? ' input-error' : ''}`}
					type="number"
					value={displayValue ?? ''}
					autoFocus={field === 'length_cm'}
					onChange={(e) => handleCellChange(partKey, partIndex, field, e.target.value)}
				/>
				{hasError && <small className="red">{hasError}</small>}
			</>
		);
	};

	const renderPartCell = (partKey, part, partIndex) => {
		const hasError = measurementErrors[partKey]?.[partIndex]?.part;
		if (!isEditable || !isRowEditing(partKey, partIndex)) {
			return (
				<>
					<p className={hasError ? 'text-danger' : ''}>{part.part || '-'}</p>
					{hasError && <small className="red">{hasError}</small>}
				</>
			);
		}
		return (
			<>
				<input
					className={`table-editable-input${hasError ? ' input-error' : ''}`}
					type="text"
					value={part.part || ''}
					autoFocus
					onChange={(e) => handleCellChange(partKey, partIndex, 'part', e.target.value)}
				/>
				{hasError && <small className="red">{hasError}</small>}
			</>
		);
	};

	const showCheckbox = (key) => {
		const toggleMeasurementType = (e) => {
			let newType = selectedMeasurementType === key ? null : key;
			if (!e.target.checked) {
				newType = key === 'CAD_CONSUMPTION' ? 'MEASUREMENT_CHART' : 'CAD_CONSUMPTION';
			}
			setSelectedMeasurementType(newType);
			toggleConsumptionTypeChange(newType);
		};

		return (
			<span style={{ width: '20px', height: '20px' }}>
				<input
					type="checkbox"
					id="measurement-breakdown-toggle"
					checked={selectedMeasurementType === key}
					onChange={toggleMeasurementType}
					style={{ marginRight: 6, cursor: 'pointer', width: 15, height: 15 }}
				/>
				<label
					htmlFor="measurement-breakdown-toggle"
					style={{ cursor: 'pointer', marginBottom: 0 }}
				/>
			</span>
		);
	};

	return (
		<LoaderComponent loading={loader}>
			<ModalContainer
				className={`width-1200-modal consumption-modal-container${loader ? ' dissabled-modal' : ''}`}
			>
				<div className="common-popup conumption-modal w-100 body-scroll">
					<div className="common-popup-header border-bottom-0 d-flex justify-content-between">
						<div className="popup-tilte w-75">
							<p className="semibold-14 gray_dark_01">Consumption Breakdown</p>
							<small className="regular-14 gray_dark_01">
								According to the measurement chart, fabric consumption has been calculated
								below:
							</small>
						</div>
						<div className="close-btn bg-white" onClick={onClose}>
							<CommonImage alt="close" src="/icons/close.svg" />
						</div>
					</div>
					<div className="common-popup-body scroll-y-label">
						{renderCadConsumption()}
						<h3 className="regular-16 mb-2 d-flex align-items-center gap-1">
							{showCheckbox('MEASUREMENT_CHART')}
							Measurement wise breakdown
							{changedPartKeys?.size > 0 && (
								<span
									title="Unsaved changes"
									className="sync-icon ml-1 cursor-pointer"
									style={{
										display: 'inline-flex',
										alignItems: 'center',
										cursor: 'default'
									}}
									onClick={() => saveMeasurement()}
								>
									{renderSyncIcon()}
								</span>
							)}
							{/*{isEditable && renderAddIcon(handleAddTable, true)}*/}
						</h3>

						{Object.entries(localMeasurements).map(([partKey, parts], index) => {
							if (!Array.isArray(parts) || parts.length === 0) return null;

							const totalConsumption = parts.reduce((sum, part) => {
								const consumption = part.final_fabric_consumption_kg;
								const value =
									typeof consumption === 'string' ? parseFloat(consumption) : consumption;
								return sum + (isNaN(value) ? 0 : value);
							}, 0);

							const partTitle = getPartTitle(`part${index + 1}`);
							const isFirstTable = index === 0;
							// true only when the mouse is over THIS table
							const isTableHovered = hoveredPartKey === partKey;

							return (
								<div
									key={partKey}
									className={`consumption-table scroll-x-label ${!isFirstTable ? 'mt-3' : ''}`}
									onMouseEnter={() => setHoveredPartKey(partKey)}
									onMouseLeave={() => setHoveredPartKey(null)}
								>
									{/* Per-table heading — Add Row icon visible only on hover */}
									<div className="d-flex align-items-center justify-content-between mb-2">
										<h3 className="regular-14 mb-0 d-flex align-items-center gap-1">
											Measurement Chart{' '}
											{measurementSizeRange || selectedMeasurementSize
												? `(Size Range: ${measurementSizeRange}, Selected Size: ${selectedMeasurementSize})`
												: ''}{' '}
											- {partTitle}
										</h3>
										{isEditable &&
											renderAddIcon(() => handleAddRow(partKey), isTableHovered)}
									</div>

									<table className="table height-sm with-border mb-0">
										<thead>
											<tr>
												<th>Body Part</th>
												<th>Length (cm)</th>
												<th>L. Allow (cm)</th>
												<th>Width (cm)</th>
												<th>W. Allow (cm)</th>
												<th>No. Fabric Pieces</th>
												<th>GSM</th>
												<th>Wastage (%)</th>
												<th>Consumption (Kg)</th>
												{isEditable && <th style={{ width: 48 }}>Action</th>}
											</tr>
										</thead>
										<tbody>
											{parts.map((part, partIndex) => {
												const isLastRow = partIndex === parts.length - 1;
												const rowIsEditing = isRowEditing(partKey, partIndex);

												return (
													<tr
														key={partIndex}
														ref={rowIsEditing ? editingRowRef : null}
														onClick={() => handleRowClick(partKey, partIndex)}
														style={{ cursor: isEditable ? 'pointer' : 'default' }}
														className={rowIsEditing ? 'row-editing' : ''}
													>
														<td className="body-part-column">
															{renderPartCell(partKey, part, partIndex)}
														</td>
														<td className="length-column">
															{renderCell(
																partKey,
																part,
																partIndex,
																'length_cm',
																part.length_cm
															)}
														</td>
														<td className="length-with-allowance-column">
															{renderCell(
																partKey,
																part,
																partIndex,
																'allowance_length_cm',
																part.allowance_length_cm
															)}
														</td>
														<td className="width-column">
															{renderCell(
																partKey,
																part,
																partIndex,
																'width_cm',
																part.width_cm
															)}
														</td>
														<td className="width-with-allowance-column">
															{renderCell(
																partKey,
																part,
																partIndex,
																'allowance_width_cm',
																part.allowance_width_cm
															)}
														</td>
														<td className="wastage-column">
															{renderCell(
																partKey,
																part,
																partIndex,
																'number_of_fabric_pieces',
																part.number_of_fabric_pieces
															)}
														</td>
														<td className="gsm-column">
															{renderCell(
																partKey,
																part,
																partIndex,
																'gsm',
																part.gsm
															)}
														</td>
														<td className="wastage-column">
															{renderCell(
																partKey,
																part,
																partIndex,
																'wastage_percentage',
																part.wastage_percentage
															)}
														</td>
														<td className={isLastRow ? 'consumption-column' : ''}>
															<p>
																{calculateTotalConsumption(part)?.toFixed(2)}
															</p>
														</td>
														{isEditable && (
															<td
																className="text-center"
																style={{ width: 48 }}
																onClick={(e) => e.stopPropagation()}
															>
																{/* Delete icon — visible only when this table is hovered */}
																<button
																	className="btn btn-sm"
																	style={{
																		padding: '2px 6px',
																		opacity: isTableHovered ? 1 : 0,
																		pointerEvents: isTableHovered
																			? 'auto'
																			: 'none',
																		transition: 'opacity 0.15s ease'
																	}}
																	onClick={() =>
																		handleDeleteRow(partKey, partIndex)
																	}
																>
																	<CommonImage
																		alt="delete"
																		src="/icons/delete_red.svg"
																		style={{ width: 16, height: 16 }}
																	/>
																</button>
															</td>
														)}
													</tr>
												);
											})}
										</tbody>
									</table>
									<div className="total-consumption table-footer p-2">
										<div className="d-flex justify-content-start">
											<p className="regular-14 gray_dark_01 weight-500">
												Total Consumption
											</p>
											<p className="regular-14 gray_dark_01 ml-2 weight-500">
												{totalConsumption.toFixed(2)} Kg
											</p>
										</div>
									</div>
								</div>
							);
						})}
					</div>
				</div>
			</ModalContainer>
		</LoaderComponent>
	);
};

export default ConsumptionModal;
